import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Wordmark } from "@/components/landing/Wordmark";
import { LocalePicker } from "@/components/landing/LocalePicker";
import { ScreenplayPatternFR } from "@/components/landing-fr/ScreenplayPatternFR";
import { fr } from "@/locales/fr";
import { appUrl } from "@/lib/referral";

// Same app destination as the English hero — the brief: "CTA buttons on
// /fr link to the same app URL the English buttons use."
const APP_SIGNUP = "https://work.voxiumscript.com";
const APP_SIGNIN = "https://work.voxiumscript.com";

export const HeroFR = () => {
  const t = fr.hero;

  const handleSeeHow = () => {
    const target = document.getElementById("section-meet-you-there-fr");
    if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative w-full min-h-screen bg-ink overflow-hidden"
      data-testid="hero-section-fr"
    >
      <div className="absolute inset-0 pointer-events-none select-none">
        <ScreenplayPatternFR className="w-full h-full" />
      </div>

      {/* Top nav row — wordmark left, sign-in + language picker right */}
      <div className="relative z-10 flex items-center justify-between px-6 sm:px-10 lg:px-16 pt-8">
        <a
          href="#hero-top-fr"
          className="inline-flex items-center tap-extend"
          aria-label="Voxium Script — accueil"
          data-testid="nav-wordmark-link-fr"
        >
          <Wordmark tone="dark" size="nav" testId="nav-wordmark-fr" />
        </a>
        <div className="flex items-center gap-6">
          <a
            href={appUrl(APP_SIGNIN, { lang: "fr" })}
            className="btn-text btn-text-light tap-extend text-sm"
            data-testid="nav-signin-link-fr"
          >
            {fr.nav.signIn}
          </a>
          <LocalePicker current="fr" />
        </div>
      </div>

      <div
        id="hero-top-fr"
        className="relative z-10 min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 sm:px-10 py-16 sm:py-20"
      >
        <div
          className="bg-cream max-w-prose3 w-full px-8 sm:px-14 py-12 sm:py-16 reveal is-visible shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)]"
          data-testid="hero-card-fr"
        >
          <div className="mb-10 sm:mb-12">
            <Wordmark tone="light" size="hero" testId="hero-wordmark-fr" />
          </div>

          <h1
            className="display-serif text-nearblack text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] mb-6 sm:mb-8"
            data-testid="hero-headline-fr"
          >
            {t.headline}
          </h1>

          <p
            className="font-body text-nearblack/85 text-base sm:text-lg leading-relaxed mb-10 sm:mb-12"
            data-testid="hero-body-fr"
          >
            {t.body}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-center">
            <a
              href={appUrl(APP_SIGNUP, { lang: "fr" })}
              className="btn-gold"
              data-testid="hero-primary-cta-fr"
            >
              {t.ctaPrimary}
              <ArrowUpRight size={16} strokeWidth={2} />
            </a>
            <button
              type="button"
              onClick={handleSeeHow}
              className="btn-text tap-extend"
              data-testid="hero-secondary-cta-fr"
            >
              {t.ctaSecondary}
            </button>
          </div>

          <div className="mt-12 sm:mt-14 pt-6 border-t border-cream-200">
            <p
              className="font-body italic text-sm text-nearblack/70"
              data-testid="hero-family-attribution-fr"
            >
              {t.attributionPrefix}
              <a
                href="https://voxiumapp.com"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-cream-200 underline-offset-4 hover:decoration-gold hover:text-gold-600 transition-colors"
              >
                {t.attributionBrand}
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroFR;
